import React from 'react';
import {
  Button,
  Grid,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Switch,
  IconButton,
  Link,
  Fade,
  ClickAwayListener,
  Paper
} from "@mui/material";
import {
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { GoogleMap } from '@react-google-maps/api';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import ElectricBikeIcon from '@mui/icons-material/ElectricBike';
import ElectricScooterIcon from '@mui/icons-material/ElectricScooter';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import MapLegend from "../../components/MapLegend";
import { useTranslation } from "react-i18next";
import Info from "../../components/Info";
import { useWeather, weatherType } from "../../constants/weatherType";
import { useGetImpactGeoJsonMap } from 'services/ContentOpenManager';
import { REFETCH_OPTIONS } from 'services/ContentManager';
import { genderCode } from 'constants/genderCode';
import { DEFAULT_LAT, DEFAULT_LNG, DEFAULT_ZOOM } from "../../constants/map";
import { CustomHoverCard, metricConfig, metricColorRanges } from './OpenDataImpact';
import { formatCompactDurationUpToYears } from '../../services/helper';
import { InfoRedAlertBox } from './OpenDataPage';
import dayjs from 'dayjs';

export default React.memo(ImpactGoogleMap);

export const impactGoogleMapContainerStyle = {
  width: '100%',
  height: '80vh',
  borderRadius: '12px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
};

const defaultCenter = {
  lat: DEFAULT_LAT,
  lng: DEFAULT_LNG
};

const overlayMapSourcesLink = {
  34 : {
    link: 'https://www.scb.se/en/services/open-data-api/open-geodata/open-data-for-deso--demographic-statistical-areas/',
    linkText: 'Statistiska centralbyrån - Open data for DeSO'
  },

}

const sessionsTypeFilters = [
  { key: 'heatmapHomeWork', value: true },
  { key: 'heatmapNoHomeWork', value: false },
  { key: 'all', value: null },
];
const genderFilters = [
  { key: 'all', value: null },
  { key: genderCode.MALE.value, value: genderCode.MALE.id },
  { key: genderCode.FEMALE.value, value: genderCode.FEMALE.id },
  { key: genderCode.UNSET.value, value: genderCode.UNSET.id }
];
function generateTooltipContent({ name, impactData, metricConfig, t, locale}) {
  const getDisplayValue = (k, cfg, value) => {
    const isPercentage = cfg.isPercentage ?? false;
    const isTime = k === 'travelTime';

    if (value == null) return '-';

    if (isPercentage) return `${(value * 100).toLocaleString(locale)}${cfg.symbol}`;
    if (isTime) return formatCompactDurationUpToYears(value, locale, false);
    return `${value.toLocaleString(locale)} ${cfg.symbol}`;
  };

  const listItems = Object.keys(impactData)
    .map(k => {
      const cfg = metricConfig[k];
      if (!cfg) return '';
      const value = impactData[k]?.value;
      const displayValue = getDisplayValue(k, cfg, value);

      return `
        <li style="margin-bottom: 6px;">
          <strong>${t(cfg.titleTranslationKey)}</strong>: ${displayValue}
        </li>`;
    })
    .join('');

  return `
    <div style="
      font-family: var(--font-family, inherit);
      background-color: var(--background, #ffffff);
      color: var(--text-primary, #000000);
      padding: 14px 16px;
      max-width: 300px;
      border-radius: 12px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    ">
      <h3 style="
        margin: 0 0 10px;
        font-size: 1.2em;
        text-align: center;
        color: var(--text-primary, #000000);
      ">
        ${name}
      </h3>

      <hr style="
        border: none;
        border-top: 1px solid var(--divider, #dddddd);
        margin-bottom: 12px;
      " />

      <ul style="
        list-style: none;
        padding: 0;
        margin: 0;
        font-size: 0.95em;
        color: var(--text-secondary, #333333);
      ">
        ${listItems}
      </ul>
    </div>
  `;
}

function ImpactGoogleMap({ center = defaultCenter, zoom = DEFAULT_ZOOM, interval, id, isAll }) {
  if (!window.infoWindow)
    window.infoWindow = new window.google.maps.InfoWindow();

  const { t, i18n } = useTranslation();
  let [filters, setFilters] = React.useState({
    isHomeWorkPath: null,
    minAge: 0,
    maxAge: 100,
    legTypes: null,
    minTime: "00:00",
    maxTime: "23:59",
    minDate: interval.start,
    maxDate: interval.end,
    startDate: dayjs.utc(interval.start).startOf('month').valueOf(),
    endDate: dayjs.utc(interval.end).add(1, 'day').valueOf(),
    weather: null,
    gender: null
  });
  const [tempFilters, setTempFilters] = React.useState({
    ...filters
  });
  const bikeTypeFilters = [
    { key: t('all'), value: null },
    { key: <PedalBikeIcon></PedalBikeIcon>, tooltip: t('bike'), value: 0 + "" },
    { key: <ElectricBikeIcon></ElectricBikeIcon>, tooltip: t('eBike'), value: 1 + "" },
    { key: <ElectricScooterIcon></ElectricScooterIcon>, tooltip: t('micromobility'), value: 2 + "" },
    { key: <DirectionsWalkIcon></DirectionsWalkIcon>, tooltip: t('byFeet'), value: 3 + "" },
    { key: <DirectionsCarIcon></DirectionsCarIcon>, tooltip: t('carpooling'), value: 4 + "" },
    { key: <DirectionsBusIcon></DirectionsBusIcon>, tooltip: t('publicTransport'), value: 5 + "" },
  ];
  const infoWindow = window.infoWindow;
  const [googleMap, setGoogleMap] = React.useState(null);
  const { status, geojson } = useGetImpactGeoJsonMap(id, filters, isAll, REFETCH_OPTIONS) || {}
  const weather = useWeather();
  const [isFullOpacity, setIsFullOpacity] = React.useState(false);
  const [selectedMetric, setSelectedMetric] = React.useState({
    metricName: Object.keys(metricConfig)[0],
    isPercentage: metricConfig.totalKm.isPercentage,
  });

  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      startDate: dayjs.utc(interval.start).startOf('month').valueOf(),
      endDate: dayjs.utc(interval.end).add(1, 'day').valueOf(),
    }));
  }, [interval]);

  React.useEffect(() => {
    if (!googleMap || (status !== 'success')) return;

    // Aggiungi il GeoJSON alla mappa
    googleMap.data.addGeoJson(geojson);

    // Imposta lo stile di default per i poligoni (opzionale)
    googleMap.data.setStyle((feature) => {
      let selectedMetricName = selectedMetric.metricName;
      const index = feature.getProperty('impactData')?.[selectedMetricName]?.quintile ?? -1;
      const givenColor = index < 0 ? 'transparent' : metricColorRanges[selectedMetricName][index];
      return {
        fillColor: givenColor,
        strokeColor: metricColorRanges[selectedMetricName][4], // sempre l'ultimo colore per il bordo
        strokeWeight: 0.5,
        fillOpacity: isFullOpacity ? 0.8 : 0.2,
        strokeOpacity: isFullOpacity ? 0.6 : 1,
        // visible: index > -1
      }
    });

    // --- definiamo i listener come variabili per poterli rimuovere
    const mouseOverListener = googleMap.data.addListener('mouseover', (event) => {
      googleMap.data.overrideStyle(event.feature, {
        strokeWeight: 2,
        fillOpacity: isFullOpacity ? 0.2 : 0.8,
        strokeOpacity: isFullOpacity ? 1 : 0.6,
      });
    });

    const mouseOutListener = googleMap.data.addListener('mouseout', () => {
      googleMap.data.revertStyle();
    });

    googleMap.data.addListener('click', (event) => {
      infoWindow.close();
      const name = event.feature.getProperty('name');
      const impactData = event.feature.getProperty('impactData') || {};
      const content = generateTooltipContent({ name, impactData, metricConfig, t, locale: i18n.language });
      infoWindow.setContent(content);
      infoWindow.setPosition(event.latLng);
      infoWindow.open(googleMap);
    });

    // Calcola i bounds per tutte le feature del GeoJSON
    const bounds = new window.google.maps.LatLngBounds();
    googleMap.data.forEach((feature) => {
      feature.getGeometry().forEachLatLng((latlng) => {
        bounds.extend(latlng);
      });
    });

    // Adatta la mappa ai bounds calcolati
    googleMap.fitBounds(bounds);

    // Imposta una restrizione per non navigare fuori dai bounds
    googleMap.setOptions({
      restriction: {
        latLngBounds: bounds,
        strictBounds: false,
      },
    });

    // --- cleanup completo
    return () => {
      googleMap.data.forEach((feature) => {
        googleMap.data.remove(feature);
      });
      mouseOverListener.remove();
      mouseOutListener.remove();
    };

  }, [googleMap, geojson, selectedMetric, infoWindow, status, isFullOpacity, t, i18n]);

  if (status === 'error' && Object.keys(geojson ?? {}).length < 1) {
    return <InfoRedAlertBox textToShow={t('impactMapNotAvailable')}></InfoRedAlertBox>
  }

  if (status === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: impactGoogleMapContainerStyle.height,
          width: impactGoogleMapContainerStyle.width,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomHoverCard id='cardMap' sx={{ width: '100%' }}>
      <Grid container justifyContent='center'>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my:1.5, gap: 1 }}>
          <Typography variant="h4">
            {t('impactMetrics')}
          </Typography>
          <Switch checked={isFullOpacity} onChange={() => setIsFullOpacity(prev => !prev)} />
          <Tooltip title={t('impactOpacitySwitch')} placement="right">
            <IconButton size="small">
              <InfoIcon fontSize="small"/>
            </IconButton>
          </Tooltip>
        </Grid>
        {Object.keys(metricConfig).map((metric) => {
          const color = metricColorRanges[metric].at(-1);
          const thisMetric = metricConfig[metric];
          const isSelected = selectedMetric.metricName === metric;

          return (
            <Grid item key={`${metric}-impact-map`} xs={4} sm={2} sx={{ mb: 1, p: 0.6 }}>
              <Tooltip title={
                <React.Fragment>
                  {t(thisMetric.descriptionTranslationKey)}<br /><br />
                  {t('impactSessionMeaning')}
                </React.Fragment>
              } placement="top" arrow>
                <Button
                  sx={{
                    backgroundColor: isSelected ? color : 'transparent',
                    borderColor: isSelected ? 'transparent' : color,
                    color: isSelected ? 'white' : color,
                    ':hover': {
                      backgroundColor: isSelected ? color : 'rgba(0, 0, 0, 0.08)',
                      borderColor: isSelected ? 'transparent' : color,
                    },
                    height: { xs: '10vh', md: '8vh', lg: '5vh', xl: 'auto' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() =>
                    setSelectedMetric({
                      metricName: metric,
                      isPercentage: thisMetric.isPercentage,
                    })
                  }
                  fullWidth
                >
                  {t(thisMetric.titleTranslationKey)}
                </Button>
              </Tooltip>
            </Grid>
          );
        })}
        <Grid item xs={12} style={{ display: 'flex', padding: 5 }}>

          <GoogleMap
            key={`${selectedMetric.metricName}-google-impact-map`}
            mapContainerStyle={impactGoogleMapContainerStyle}
            center={center}
            zoom={zoom}
            onLoad={(mapInstance) => setGoogleMap(mapInstance)}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              styles: [
                {
                  featureType: 'poi',
                  stylers: [{ visibility: 'off' }]
                },
                {
                  featureType: 'transit',
                  stylers: [{ visibility: 'off' }]
                }
              ]
            }}
          >
            <MapLegend
              legendTitle={t('filters')}
              labels={[
                {
                  title: <>{t('weather')} <Info>{t('weatherInfo')}</Info></>,
                  actionButtons: {
                    value: tempFilters.weather,
                    buttons: Object.values(weatherType).map(
                      w => ({
                        value: w,
                        onClick: () => setTempFilters({ ...tempFilters, weather: w }),
                        icon: weather[w].icon,
                        tooltip: weather[w].tooltip
                      })
                    )
                  }
                },
                {
                  title: t('vehicleType'),
                  actionButtons: {
                    value: tempFilters.legTypes,
                    buttons: bikeTypeFilters.map(
                      option => ({
                        value: option.value,
                        onClick: () => setTempFilters({
                          ...tempFilters,
                          legTypes: option.value,
                        }),
                        icon: option.key,
                        tooltip: option.tooltip
                      })
                    )
                  }
                },
                {
                  title: t('sessionFilter'),
                  actionButtons: {
                    value: tempFilters.isHomeWorkPath,
                    buttons: sessionsTypeFilters.map(
                      option => ({
                        value: option.value,
                        onClick: () => setTempFilters({
                          ...tempFilters,
                          isHomeWorkPath: option.value,
                        }),
                        icon: t(option.key)
                      })
                    )
                  }
                },
                {
                  title: t('gender'),
                  actionButtons: {
                    value: tempFilters.gender,
                    buttons: genderFilters.map(
                      option => ({
                        value: option.value,
                        onClick: () => setTempFilters({
                          ...tempFilters,
                          gender: option.value,
                        }),
                        icon: t(option.key)
                      })
                    )
                  }
                }
              ]}
              sliderLabels={[
                {
                  title: t('age'),
                  value: [tempFilters.minAge, tempFilters.maxAge],
                  onChange: (newRange) => {
                    setTempFilters({
                      ...tempFilters,
                      minAge: newRange[0],
                      maxAge: newRange[1]
                    })
                  },
                  marks: [
                    { value: 0, label: '0' },
                    { value: 100, label: '100' },
                  ],
                }

              ]}
              actionButton={
                <Button variant="contained"
                  sx={{
                    maxWidth: 'fit-content'
                  }}
                  onClick={() => setFilters({ ...tempFilters })}>
                  {t('applyFilters')}
                </Button>
              }
            />
            <MapInfoPopup
              headerText={t('overlayMapSource')}
              link={overlayMapSourcesLink[id].link}
              linkText={overlayMapSourcesLink[id].linkText} />
          </GoogleMap>
        </Grid>
      </Grid>
    </CustomHoverCard>
  );
};

function MapInfoPopup  ({headerText, link, linkText})  {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 22,
        bottom: 30,
        zIndex: 1,
        opacity: 0, //opacità iniziale
        animation: 'fadeInPosition 0.3s ease-out 0.1s forwards',
        '@keyframes fadeInPosition': {
          '0%': {
            opacity: 0,
            transform: 'translateY(10px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}
    >
      {/* Icona Info */}
      <IconButton
        onClick={handleToggle}
        sx={{
          backgroundColor: 'white',
          boxShadow: 2,
          width: 36,
          height: 36,
          '&:hover': {
            backgroundColor: 'grey.100',
          },
        }}
      >
        <InfoIcon color="primary" />
      </IconButton>

      {/* Popup */}
      <Fade in={isOpen}>
        <Paper
          sx={{
            position: 'absolute',
            bottom: 56, // Sopra l'icona
            left: 0,
            minWidth: 240,
            maxWidth: 320,
            p: 2,
            boxShadow: 3,
            borderRadius: 2,
            // Freccia che punta verso l'icona
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 16,
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid white',
            }
          }}
        >
          {isOpen && 
          <ClickAwayListener onClickAway={handleClose}>
            <Box>
              {/* Header con bottone chiudi */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {headerText}
                </Typography>
                <IconButton 
                  onClick={handleClose} 
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Contenuto */}
              <Typography variant="body2" color="text.secondary">
                <Link
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontWeight: 500 }}
                >
                  {linkText}
                </Link>
              </Typography>
            </Box>
          </ClickAwayListener>
          }
        </Paper>
      </Fade>
    </Box>
  );
};